import { ReactNode } from 'react';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { manageProductTag, removeProductTag } from 'api/operations/productServiceV2';
import CartItemDelegate, { ProductTagInfo } from '../delegates/cartItemDelegate';
import IProposalPageStore from '../iProposalPageStore';

interface ManageTagFields extends DefaultFormFields {
  tag: FormControl<string>;
}

interface Stores<PageStore extends IProposalPageStore> {
  pageStore: PageStore;
  cartItemDelegate: CartItemDelegate<PageStore>;
}

const INITIAL_TAG_INFO: ProductTagInfo = {
  productId: '',
  tag: '',
  periodId: '',
};

export default class ManageTagModalStore<PageStore extends IProposalPageStore> {
  private pageStore: PageStore;
  private cartItemDelegate: CartItemDelegate<PageStore>;
  private _isLoading = false;
  private _tagData: ProductTagInfo = { productId: '', periodId: '', tag: '' };

  private form: FormController<ManageTagFields> = new FormController({
    tag: new FormControl<string>('', { validations: ['printableCharacters'], trim: true }),
  });

  private resetTagInfo(): void {
    this._tagData = INITIAL_TAG_INFO;
    this.form.resetForm();
  }

  public async updateCartItem(): Promise<void> {
    const portfolioId = this.cartItemDelegate.portfolioId;
    await this.pageStore.updateCartItem(portfolioId, 'no-cache');
  }

  public closeModal(): void {
    this.cartItemDelegate.createManageTagModalDelegate.close();
  }

  public setLoading(status: boolean): void {
    this._isLoading = status;
  }

  public setTag = this.form.onFieldChange;

  public setTagData({ periodId, tag, productId }: ProductTagInfo): void {
    if (tag) {
      this.form.fields.tag.overrideValue(tag);
    }
    runInAction(() => {
      this._tagData.periodId = periodId;
    });
    runInAction(() => {
      this._tagData.productId = productId;
    });
    runInAction(() => {
      this._tagData.tag = tag;
    });
  }

  public isDataValid(): boolean {
    return this.form.isFormValid();
  }

  public async manageTag(): Promise<void> {
    this.setLoading(true);
    const { productId, periodId } = this._tagData;
    const portfolioId = this.cartItemDelegate.portfolioId;
    await manageProductTag({
      tag: this.formTag,
      portfolioId,
      productId,
      periodId,
    });

    await runInAction(async () => {
      await this.pageStore.updateCartItem(portfolioId, 'no-cache');
      this.finishJob();
    });
  }

  public async removeTag(): Promise<void> {
    this.setLoading(true);

    const portfolioId = this.cartItemDelegate.portfolioId;
    const removeProductData = {
      portfolioId,
      productId: this._tagData.productId,
      periodId: this._tagData.periodId,
    };
    await removeProductTag(removeProductData);
    await this.updateCartItem();
    this.finishJob();
  }

  private finishJob(): void {
    this.setLoading(false);
    this.closeModal();
    this.resetTagInfo();
  }

  get formTag(): string {
    return this.form.fields.tag.value;
  }

  get currentTag(): string {
    return this._tagData.tag;
  }

  get tagError(): ReactNode {
    return this.form.fields.tag.unpassedValidationMsg;
  }

  get modalIsVisible(): boolean {
    return this.cartItemDelegate.createManageTagModalDelegate.isVisible;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get isTagAlreadyAdded(): boolean {
    return !!this._tagData.tag;
  }

  constructor({ pageStore, cartItemDelegate }: Stores<PageStore>) {
    this.pageStore = pageStore;
    this.cartItemDelegate = cartItemDelegate;

    autorun(() => {
      const { activeProductInfo } = this.cartItemDelegate;
      this.setTagData(activeProductInfo);
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
